/** @file classify: one FacetPick per impostor from the PoseBuffer columns and the camera (frozen entry). */
import { bandOf } from '@zilo/pose3d-format/tables/bandOf';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import { variantMaskOf } from '@zilo/pose3d-pose/attach/variantMaskOf';
import { POSE_FLAGS } from '@zilo/pose3d-pose/layout/poseFlags';
import { POSE_LAYOUT } from '@zilo/pose3d-pose/layout/poseLayout';
import { nodeAngles } from '@zilo/pose3d-facet/azimuth/nodeAngles';
import type { Classify, FacetPick } from '@zilo/pose3d-facet/contract';
import { activeVariants } from '@zilo/pose3d-facet/entries/activeVariants';
import { entryFor } from '@zilo/pose3d-facet/entries/entryFor';
import { foldYawFlip } from '@zilo/pose3d-facet/pitch/foldYawFlip';
import { pitchRelative } from '@zilo/pose3d-facet/pitch/pitchRelative';
import type { FacetItem, FacetScratch } from '@zilo/pose3d-facet/prepare/facetPlanTypes';
import { prepareFacets } from '@zilo/pose3d-facet/prepare/prepareFacets';
import { sectorPick } from '@zilo/pose3d-facet/sector/sectorPick';
import { depthOffsetOf } from '@zilo/pose3d-facet/sheet/depthOffsetOf';
import { effectiveRotate } from '@zilo/pose3d-facet/sheet/effectiveRotate';
import { entryRect } from '@zilo/pose3d-facet/sheet/entryRect';
import { pivotOf } from '@zilo/pose3d-facet/sheet/pivotOf';
import { visibilityOf } from '@zilo/pose3d-facet/visibility/visibilityOf';

/** A blank pick for `drawable`, filled by the first classify into it. */
function newPick(drawable: string): FacetPick {
  return {
    drawable, entry: -1, tile: -1, row: -1, frame: 0, flip: false, sheet: '', rect: [0, 0, 0, 0], sideBack: 0, depthOffset: 0,
    drawnAzimuthDeg: 0, drawnScreenAngleDeg: 0, pivot: [0, 0], rotate: 'none', cut: 'none', cutInverse: false, hidden: false, missing: false,
  };
}

/** Writes the pick of an impostor no entry serves (`hidden` on purpose, else `missing`). */
function writeNoEntry(pick: FacetPick, frame: number, hidden: boolean): void {
  pick.entry = -1; pick.tile = -1; pick.row = -1; pick.frame = frame; pick.flip = false; pick.sheet = '';
  pick.rect[0] = 0; pick.rect[1] = 0; pick.rect[2] = 0; pick.rect[3] = 0;
  pick.sideBack = 0; pick.depthOffset = 0; pick.drawnAzimuthDeg = 0; pick.drawnScreenAngleDeg = 0;
  pick.pivot[0] = 0; pick.pivot[1] = 0;
  pick.rotate = 'none'; pick.cut = 'none'; pick.cutInverse = false;
  pick.hidden = hidden; pick.missing = !hidden;
}

/** Writes the pick of entry `s.choice.entry` looked up at the (folded when it folds) azimuth. */
function writeEntry(pick: FacetPick, item: FacetItem, s: FacetScratch, flags: number, hidden: boolean): void {
  const index = s.choice.entry;
  const entry = item.drawable.facets[index] as FacetEntry;
  const folded = s.choice.folded;
  const table = folded ? item.flipTables[index] : item.tables[index];
  if (table === undefined) return;
  const sp = sectorPick(table, entry, folded ? s.fold.yawDeg : s.angles.azimuthDeg, s.sector);
  pick.entry = index; pick.tile = sp.tile; pick.row = 0; pick.frame = s.choice.frame; pick.flip = sp.flip;
  pick.sheet = entry.sheet;
  entryRect(entry, sp.tile, pick.rect);
  pick.sideBack = sp.sideBack;
  pick.depthOffset = depthOffsetOf(entry, sp.sideBack);
  pick.drawnAzimuthDeg = sp.drawnAzimuthDeg; pick.drawnScreenAngleDeg = sp.drawnScreenAngleDeg;
  pivotOf(entry, pick.pivot);
  pick.rotate = effectiveRotate(entry, (flags & POSE_FLAGS.rotToggle) !== 0);
  pick.cut = entry.cut; pick.cutInverse = entry.cutInverse ?? false;
  pick.hidden = hidden || sp.tile < 0;
  pick.missing = !pick.hidden && item.filled[index]?.[sp.tile] !== true;
}

/**
 * The frozen `Classify` (plan B6): per impostor, azimuth = yaw column + camera yaw; pitch relative to the profile
 * camera (`pitchRelative`), its band (always of the unfolded pitch) and the fold (`foldYawFlip`, 18.4); the frame key, rotToggle and hidden
 * bits from `flags`; `entryFor` by variant (the drawable's mask: `variantMaskOf`, so an attachment's own
 * `attachmentVariants` entry wins over the host's `variantMask`, 22.3), frame key and band; `sectorPick` on the entry's table (its FLIP twin at
 * azimuth + 180 when it folds); rect, pivot, depth offset and rotate rule from the entry. `hidden`: node hidden, tile -1,
 * `onMissingFrame: 'hide'`, or attachment visibility (`visibilityOf`); `missing`: not hidden and no entry or no image
 * in the cell's slot. `row` is the strip row (0; -1 without entry). Reuses `out[i]`; `out.length` = impostor count.
 */
export const classify: Classify = (puppet, pose, camera, params, out) => {
  const plan = prepareFacets(puppet);
  const s = plan.scratch;
  const hostMask = params.variantMask ?? 0;
  const active = activeVariants(puppet.variants, hostMask, plan.active);
  const perAttachment = params.attachmentVariants !== undefined;
  const deltaDeg = params.profile.camera.pitch - camera.pitchDeg;
  for (let i = 0; i < plan.items.length; i++) {
    const item = plan.items[i] as FacetItem;
    const pick = out[i] ?? newPick(item.drawable.id);
    out[i] = pick;
    pick.drawable = item.drawable.id;
    nodeAngles(pose, item.nodeIndex, camera.yawDeg, s.angles);
    const pitchRel = pitchRelative(s.angles.pitchDeg, deltaDeg);
    foldYawFlip(s.angles.azimuthDeg, pitchRel, s.fold);
    const flags = pose[item.nodeIndex * POSE_LAYOUT.stride + POSE_LAYOUT.flags] ?? 0;
    const frameKey = (flags >> POSE_FLAGS.frameKeyShift) & POSE_FLAGS.frameKeyMask;
    const mask = perAttachment ? variantMaskOf(puppet, item.drawable.id, params) : hostMask;
    const variants = mask === hostMask ? active : activeVariants(puppet.variants, mask, s.altActive);
    entryFor(item.drawable.facets, frameKey, bandOf(pitchRel), s.fold.flipped, variants,
      item.drawable.onMissingFrame ?? 'default', s.choice);
    const hidden = s.choice.hidden || (flags & POSE_FLAGS.hidden) !== 0
      || !visibilityOf(item.drawable.id, puppet, pose, plan.nodeIndex, params.weaponGroup);
    if (s.choice.entry < 0) writeNoEntry(pick, s.choice.frame, hidden);
    else writeEntry(pick, item, s, flags, hidden);
  }
  out.length = plan.items.length;
  return out;
};
